/**
 * CUSTOMIZATION: Presidio mini-cart chrome coordination.
 * Lets ATC (and similar) open the drawer immediately on add-to-cart.
 * Fires GA4 `view_cart` when the drawer opens and the `tracking` feature is on.
 * Announces which SKU/qty was added for Baymard-style confirmation feedback.
 */
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { FeatureToggleService } from 'ish-core/feature-toggle.module';
import { GoogleTagManagerService } from 'ish-core/services/google-tag-manager/google-tag-manager.service';

export interface PresidioAddedToCartAnnouncement {
  sku: string;
  name: string;
  quantity: number;
}

@Injectable({ providedIn: 'root' })
export class PresidioMiniCartChromeService {
  private readonly checkoutFacade = inject(CheckoutFacade);
  private readonly gtmService = inject(GoogleTagManagerService);
  private readonly featureToggle = inject(FeatureToggleService);
  private readonly openRequests = new Subject<void>();
  private readonly announcementSubject = new BehaviorSubject<PresidioAddedToCartAnnouncement | undefined>(undefined);

  /** Emits when a shopper action should reveal the mini-cart drawer now. */
  readonly openRequests$ = this.openRequests.asObservable();

  /** Latest add-to-cart confirmation (SKU / name / qty). */
  readonly announcement$ = this.announcementSubject.asObservable();

  requestOpen() {
    this.openRequests.next();
  }

  /** Open the drawer and surface which item was just added. */
  announceAdded(item: PresidioAddedToCartAnnouncement) {
    this.announcementSubject.next(item);
    this.requestOpen();
  }

  clearAnnouncement() {
    this.announcementSubject.next(undefined);
  }

  /**
   * GA4 `view_cart` when the mini-cart drawer becomes visible.
   * Classification: NEW DIAS-ALIGNED CAPABILITY (DIAS had no mini-cart view_cart).
   * Honors the same `tracking` feature gate as DIAS GTM effects.
   * Waits until basket loading settles so we do not emit an empty cart snapshot mid-hydrate.
   */
  trackViewCart() {
    if (SSR) {
      return;
    }
    this.featureToggle
      .enabled$('tracking')
      .pipe(
        take(1),
        filter(enabled => enabled),
        switchMap(() =>
          combineLatest([this.checkoutFacade.basketLoading$, this.checkoutFacade.basketLineItems$]).pipe(
            filter(([loading]) => !loading),
            take(1),
            map(([, items]) => items ?? [])
          )
        )
      )
      .subscribe(items => {
        this.gtmService.triggeringListItemEvent('view_cart', items);
      });
  }
}
